import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Zap, Building, Factory, Droplet, Lightbulb, Wrench, ArrowRight, Sun, Clipboard, Tractor } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { services as mockServices } from '../mockData';
import { getServices } from '../services/api';

const iconMap = {
  home: Zap,
  building: Building,
  factory: Factory,
  droplet: Droplet,
  lightbulb: Lightbulb,
  wrench: Wrench,
  zap: Zap,
  sun: Sun,
  clipboard: Clipboard,
  tractor: Tractor
};

export const Services = () => {
  const [services, setServices] = useState(mockServices);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        if (data.length > 0) setServices(data);
      } catch (error) {
        console.log('Using mock services');
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 via-blue-50 to-white">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            Our <span className="text-orange-500">Services</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive solar EPC solutions designed to meet your energy needs. 
            From consultation to installation and maintenance, we handle it all.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Zap;
              return (
                <Card key={service.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    
                    {service.details && (
                      <p className="text-sm text-gray-500 mb-6 leading-relaxed">{service.details}</p>
                    )}
                    
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-orange-600 mb-3">Key Features:</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Our <span className="text-orange-500">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, transparent, and efficient solar installation process
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { step: '01', title: 'Site Survey', desc: 'Our experts visit your location to assess feasibility and requirements' },
              { step: '02', title: 'Design & Quote', desc: 'Customized system design with detailed quotation and ROI analysis' },
              { step: '03', title: 'Installation', desc: 'Professional installation by certified technicians with quality materials, right tools & PPE' },
              { step: '04', title: 'Govt Approvals', desc: 'Comprehensive approval services from Nodal Agencies, Discom, Transco, CEIG' },
              { step: '05', title: 'Support', desc: 'Ongoing maintenance, monitoring, and customer support services' }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className="text-6xl font-bold text-orange-100 mb-4">{item.step}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
                {index < 4 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-orange-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <Card className="border-none shadow-2xl bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
                Contact us today for a free consultation and personalized solar solution for your needs
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/calculator">
                  <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl font-semibold shadow-xl">
                    Calculate Savings
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-6 text-lg rounded-xl font-semibold">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
